import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import Card from './Card';
import Button from './Button';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

export function TableToolbar({
  search,
  onSearch,
  placeholder = 'Search...',
  filters,
  actions,
}) {
  return (
    <div className="table-toolbar">
      <div className="toolbar-row">
        {onSearch && (
          <div className="ui-search toolbar-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={placeholder}
            />
          </div>
        )}
        {filters && <div>{filters}</div>}
        {actions && <div>{actions}</div>}
      </div>
    </div>
  );
}

function makeCellRenderer(renderFn) {
  function Renderer(params) {
    if (!params.data) return null;
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        padding: '2px 0',
      }}>
        {renderFn(params.data)}
      </div>
    );
  }
  Renderer.displayName = 'Renderer';
  return Renderer;
}

export default function DataTable({
  title,
  subtitle,
  toolbar,
  columns = [],
  rows = [],
  loading,
  empty = 'No records found',
  rowKey = 'id',
  footer,
  onGridReady,
  getRowClass,   // (rowData) => 'css-class-name' — used for row highlighting
  // NOTE: getRowStyle is intentionally NOT passed to AgGridReact.
  // AG Grid's getRowStyle expects a plain CSS object, not a string or array.
  // Use getRowClass + CSS instead (reliable across all AG Grid versions).
}) {
  const gridRef = useRef(null);
  const gridReadyRef = useRef(false);

  useEffect(() => {
    if (gridReadyRef.current && gridRef.current && gridRef.current.api) {
      try {
        gridRef.current.api.setRowData(rows || []);
      } catch (e) {
        console.warn('setRowData failed', e);
      }
    }
  }, [rows]);

  const frameworkComponents = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      if (col.render) {
        map[`renderer_${col.key}`] = makeCellRenderer(col.render);
      }
    });
    return map;
  }, [columns]);

  const columnDefs = useMemo(() => {
    return columns.map((col) => {
      const base = {
        field: col.key,
        colId: col.key,
        headerName: col.header,
        minWidth: col.minWidth || 140,
        flex: col.flex || 1,
        resizable: true,
        sortable: col.sortable !== false,
        filter: col.filter !== false,
        floatingFilter: col.floatingFilter === true,
        suppressMenu: false,
        suppressSizeToFit: false,
        menuTabs: ['filterMenuTab'],
      };

      if (col.render) {
        return {
          ...base,
          cellRenderer: `renderer_${col.key}`,
          valueGetter: col.valueGetter
            ? col.valueGetter
            : (params) => params.data?.[col.key],
        };
      }

      return {
        ...base,
        valueGetter: col.valueGetter
          ? col.valueGetter
          : (params) => {
              const val = params.data?.[col.key];
              if (val == null) return '';
              if (typeof val === 'string' || typeof val === 'number') return String(val);
              if (Array.isArray(val)) return val.join(', ');
              if (typeof val === 'object') return Object.values(val).filter(Boolean).join(' ');
              return '';
            },
      };
    });
  }, [columns]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
  }), []);

  const getRowId = useCallback(
    (params) => String(params.data?.[rowKey] ?? params.data?.id ?? Math.random()),
    [rowKey]
  );

  // AG Grid getRowClass — receives { data, node } and returns a CSS class string
  const agGetRowClass = useCallback(
    (params) => {
      if (!getRowClass || !params.data) return '';
      const cls = getRowClass(params.data);
      return typeof cls === 'string' ? cls : '';
    },
    [getRowClass]
  );

  const handleGridReady = useCallback((params) => {
    gridReadyRef.current = true;
    setTimeout(() => {
      try {
        params.api.sizeColumnsToFit();
      } catch (e) {
        console.warn('Grid init failed', e);
      }
    }, 100);
    onGridReady?.(params);
  }, [onGridReady]);

  return (
    <Card className="data-card">
      {(title || subtitle) && (
        <div style={{ marginBottom: 12 }}>
          {title && <h2 style={{ margin: 0 }}>{title}</h2>}
          {subtitle && (
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {toolbar}

      <div className="ag-theme-alpine" style={{ width: '100%', minHeight: 400 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
            Loading...
          </div>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            frameworkComponents={frameworkComponents}
            rowHeight={70}
            headerHeight={35}
            domLayout="autoHeight"
            animateRows={false}
            suppressRowClickSelection={true}
            suppressCellFocus={true}
            enableCellTextSelection={true}
            overlayNoRowsTemplate={`<span style="color:#6b7280;padding:20px;display:block">${empty}</span>`}
            onGridReady={handleGridReady}
            getRowClass={agGetRowClass}
            // ⚠️ DO NOT add getRowStyle here — it causes
            // "Failed to set an indexed property on CSSStyleDeclaration"
            // when the value is a string or array. Use getRowClass + CSS only.
          />
        )}
      </div>

      {footer}
    </Card>
  );
}

export function Pagination({ page, total, limit, count, onPrev, onNext, prevLabel, nextLabel }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));

  return (
    <div className="table-pagination">
      <p>
        Showing <b>{count || 0}</b> of <b>{total || 0}</b>
      </p>
      <div className="pagination-controls">
        <Button disabled={page <= 1} onClick={onPrev}>
          <ChevronLeft size={16} /> {prevLabel || 'Prev'}
        </Button>
        <span>{page} / {totalPages}</span>
        <Button disabled={page >= totalPages} onClick={onNext}>
          {nextLabel || 'Next'} <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}